"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
function uuidv4() {
  return crypto.randomUUID();
}
import type { AiProfile } from "@/types/ai";
import type { VaultFile } from "@/types/vault";
import { db } from "@/lib/db";
import {
  selectVaultFolder,
  listFilesInDirectory,
  readFileFromHandle,
  createFileInput,
} from "@/lib/vault/fileAccess";
import { extractTextFromFile } from "@/lib/vault/ingest";
import { chunkText } from "@/lib/vault/chunk";
// Dynamic import to prevent server-side loading of transformers/sharp
// Do NOT import generateEmbeddings at top level - it will cause sharp to load

interface Props {
  profile: AiProfile;
}

export default function VaultManager({ profile }: Props) {
  const router = useRouter();
  const [files, setFiles] = useState<VaultFile[]>([]);
  const [indexing, setIndexing] = useState(false);
  const [lastIndexed, setLastIndexed] = useState<Date | null>(null);

  useEffect(() => {
    loadFiles();
  }, [profile.id]);

  async function loadFiles() {
    const vaultFiles = await db.vaultFiles
      .where("profileId")
      .equals(profile.id)
      .toArray();
    setFiles(vaultFiles);

    // Find most recent indexed file
    const indexed = vaultFiles.filter((f) => f.status === "indexed");
    if (indexed.length > 0) {
      const mostRecent = indexed.sort((a, b) => b.addedAt - a.addedAt)[0];
      setLastIndexed(new Date(mostRecent.addedAt));
    }
  }

  async function handleSelectFolder() {
    try {
      const dirHandle = await selectVaultFolder();
      if (!dirHandle) return;

      setIndexing(true);
      const fileHandles = await listFilesInDirectory(dirHandle);

      for (const fileHandle of fileHandles) {
        await processFile(fileHandle, "folder");
      }

      await loadFiles();
    } catch (error: any) {
      console.error("Folder selection error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setIndexing(false);
    }
  }

  async function handleUploadFiles() {
    try {
      const fileList = await createFileInput(".pdf,.docx,.txt,.md", true);
      if (!fileList) return;

      setIndexing(true);

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        await processUploadedFile(file);
      }

      await loadFiles();
    } catch (error: any) {
      console.error("Upload error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setIndexing(false);
    }
  }

  async function processFile(
    fileHandle: FileSystemFileHandle,
    source: "folder" | "upload"
  ) {
    const file = await readFileFromHandle(fileHandle);
    await processUploadedFile(file, source);
  }

  async function processUploadedFile(
    file: File,
    source: "folder" | "upload" = "upload"
  ) {
    // Check if file already exists
    const existing = await db.vaultFiles
      .where("profileId")
      .equals(profile.id)
      .filter((f) => f.filename === file.name)
      .first();

    if (existing) {
      // Skip if already indexed
      if (existing.status === "indexed") {
        return;
      }
      // Delete old chunks if re-indexing
      await db.vaultChunks
        .where("fileId")
        .equals(existing.id)
        .delete();
      await db.vaultFiles.delete(existing.id);
    }

    // Create vault file record
    const vaultFile: VaultFile = {
      id: uuidv4(),
      profileId: profile.id,
      filename: file.name,
      mimeType: file.type || "application/octet-stream",
      source,
      addedAt: Date.now(),
      status: "pending",
    };

    await db.vaultFiles.add(vaultFile);

    try {
      // Extract text
      const text = await extractTextFromFile(file);

      // Chunk text
      const chunks = chunkText(text, 1000, 200);

      // Generate embeddings - dynamic import to prevent server-side loading
      const texts = chunks.map((c) => c.text);
      const { generateEmbeddings } = await import("@/lib/vault/embed");
      const embeddings = await generateEmbeddings(texts);

      // Store chunks
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const embedding = embeddings[i];

        await db.vaultChunks.add({
          id: uuidv4(),
          profileId: profile.id,
          fileId: vaultFile.id,
          text: chunk.text,
          embedding,
          tokenEstimate: Math.ceil(chunk.text.length / 4),
          chunkIndex: chunk.index,
          createdAt: Date.now(),
        });
      }

      // Update file status
      await db.vaultFiles.update(vaultFile.id, { status: "indexed" });
      setLastIndexed(new Date());
    } catch (error: any) {
      console.error(`Error indexing ${file.name}:`, error);
      await db.vaultFiles.update(vaultFile.id, {
        status: "error",
        errorMessage: error.message,
      });
    }
  }

  const indexedCount = files.filter((f) => f.status === "indexed").length;
  const errorCount = files.filter((f) => f.status === "error").length;

  return (
    <div className="min-h-screen gradient-bg p-4 relative overflow-hidden pb-24">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => router.push("/chat")}
            className="mb-6 px-4 py-2 rounded-xl glass-input dark:glass-input-dark text-white/90 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm font-medium"
          >
            ← Back to Chat
          </button>
          <h1 className="text-4xl font-bold mb-3 text-white">Knowledge</h1>
          <p className="text-white/80 text-lg">
            Your documents are indexed locally and never uploaded to any server.
          </p>
        </div>

        {/* Actions */}
        <div className="glass-card dark:glass-card-dark rounded-3xl p-8 mb-6 premium-shadow dark:premium-shadow-dark backdrop-blur-xl">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleSelectFolder}
              disabled={indexing}
              className="flex-1 px-6 py-4 rounded-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-semibold hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-purple-500/50 hover:shadow-xl hover:shadow-purple-500/60 hover:scale-105 active:scale-95"
            >
              {indexing ? "Indexing..." : "Choose Knowledge Folder"}
            </button>
            <button
              onClick={handleUploadFiles}
              disabled={indexing}
              className="flex-1 px-6 py-4 rounded-xl glass-input dark:glass-input-dark text-white/90 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 backdrop-blur-sm font-medium"
            >
              Upload Files
            </button>
          </div>

          {lastIndexed && (
            <p className="mt-6 text-sm text-white/70">
              Last indexed: {lastIndexed.toLocaleString()}
            </p>
          )}

          <p className="mt-4 text-sm text-white/60 italic">
            Nothing is uploaded. This stays on your device.
          </p>
        </div>

        {/* File list */}
        <div className="glass-card dark:glass-card-dark rounded-3xl p-8 premium-shadow dark:premium-shadow-dark backdrop-blur-xl">
          <h2 className="text-2xl font-bold mb-6 text-white">
            Indexed Files ({indexedCount})
          </h2>

          {files.length === 0 ? (
            <p className="text-white/70 text-center py-12 text-lg">
              No files indexed yet. Choose a folder or upload files to get
              started.
            </p>
          ) : (
            <div className="space-y-3">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-5 rounded-2xl glass-input dark:glass-input-dark border border-white/20 backdrop-blur-sm hover:bg-white/10 transition-all duration-200"
                >
                  <div className="flex-1">
                    <div className="font-semibold text-white mb-1">{file.filename}</div>
                    <div className="text-sm text-white/60">
                      {file.source} •{" "}
                      {new Date(file.addedAt).toLocaleDateString()}
                    </div>
                    {file.errorMessage && (
                      <div className="text-sm text-red-300 mt-2">
                        Error: {file.errorMessage}
                      </div>
                    )}
                  </div>
                  <div>
                    {file.status === "indexed" && (
                      <span className="px-3 py-1.5 text-xs rounded-xl bg-green-500/20 text-green-300 border border-green-500/30 backdrop-blur-sm">
                        Indexed
                      </span>
                    )}
                    {file.status === "pending" && (
                      <span className="px-3 py-1.5 text-xs rounded-xl bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 backdrop-blur-sm">
                        Pending
                      </span>
                    )}
                    {file.status === "error" && (
                      <span className="px-3 py-1.5 text-xs rounded-xl bg-red-500/20 text-red-300 border border-red-500/30 backdrop-blur-sm">
                        Error
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

